/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Hightouch.Spelling = NO -->*/
/*<!-- vale Hightouch.Spelling = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669593600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 28 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "zuora",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Zuora object notes",
          "depth": 1,
          "id": "zuora-object-notes"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      strong: "strong",
      img: "img",
      h4: "h4",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "After selecting Zuora from our Destinations catalog, you will be prompted to enter your Zuora Base URL, Client ID, and Client Secret."
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "https://www.zuora.com/developer/api-reference/#section/Introduction/Access-to-the-API",
          children: "this table"
        }), " from Zuora to select the correct Zuora URL, and follow\n", _jsx(_components.a, {
          href: "https://knowledgecenter.zuora.com/Billing/Tenant_Management/A_Administrator_Settings/Manage_Users?_gl=1%2Ap9mz1o%2A_ga%2AMzI5ODc0MjYxLjE2NDY3NjQxMDI.%2A_ga_MY8CQ650DH%2AMTY1NDA0MTU1MC40My4xLjE2NTQwNDE2MTIuMA..&_ga=2.23071486.552820277.1653943857-329874261.1646764102#Create_an_OAuth_Client_for_a_User",
          children: "these steps"
        }), "\nto create an OAuth Client in Zuora that will provide you with the required Client ID and Secret."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Zuora objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Accounts"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Subscriptions"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Usage"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "Here are the possible modes for how to alter Outreach objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": pushes new objects to Zuora and updates fields that change in your warehouse.", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["Supported for ", _jsx(_components.code, {
                children: "Accounts"
              }), " and ", _jsx(_components.code, {
                children: "Subscriptions"
              }), "."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": updates particular fields on existing objects in Zuora. It doesn't add new objects.", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["Supported for ", _jsx(_components.code, {
                children: "Accounts"
              }), ", ", _jsx(_components.code, {
                children: "Subscriptions"
              }), ", and ", _jsx(_components.code, {
                children: "Usage"
              }), "."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": adds new objects in Zuora.", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["Supported for ", _jsx(_components.code, {
                children: "Usage"
              }), "."]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Records can be matched from your source to your Zuora workspace by the given fields in the dropdown."
      }), "\n", _jsx(_components.p, {
        children: "The following are supported record ID's for each object:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.code, {
              children: "Accounts"
            })
          }), ": ", _jsx(_components.code, {
            children: "Account Number"
          }), ", ", _jsx(_components.code, {
            children: "Account ID"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.code, {
              children: "Subscription"
            })
          }), ": ", _jsx(_components.code, {
            children: "Subscription Number"
          }), ", ", _jsx(_components.code, {
            children: "Subscription ID"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.code, {
              children: "Usage"
            })
          }), ": ", _jsx(_components.code, {
            children: "Usage Record ID"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zuora-record-id-match.png",
          alt: ""
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["A record ID is required when performing ", _jsx(_components.code, {
            children: "Upsert"
          }), " or ", _jsx(_components.code, {
            children: "Update"
          }), " actions, but not\n", _jsx(_components.code, {
            children: "Insert"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Zuora's object properties, including\ncustom fields."
      }), "\n", _jsx(_components.h4, {
        id: "required-fields",
        children: "Required fields"
      }), "\n", _jsxs(_components.p, {
        children: ["Each object / action pair will have their own required properties to be mapped. These required properties will be in their own mapping section during sync configuration. Be sure to scroll down to our ", _jsx(_components.a, {
          href: "#object-notes",
          children: "Zuora Object Notes"
        }), " to see which fields are required, and their respective data types."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zuora-required-mappings.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "optional-and-custom-fields",
        children: "Optional and custom fields"
      }), "\n", _jsxs(_components.p, {
        children: ["Any optional built-in fields or custom fields sent to Zuora will be mapped in the custom mappings section of sync configuration. Scroll down to our ", _jsx(_components.a, {
          href: "#object-notes",
          children: "Zuora Object Notes"
        }), " to see Zuora's optional built-in fields, and their respective data types."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zuora-custom-mappings.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "helpful-tip",
        children: "Helpful tip:"
      }), "\n", _jsx(_components.p, {
        children: "Use our advanced mapper to fill in static values for numbers or string values that may be consistent across your sync, that may not have a column in your data warehouse. See the example below."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zuora-advanced-mapper.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "zuora-object-notes",
        children: "Zuora object notes"
      }), "\n", _jsx(_components.h4, {
        id: "accounts",
        children: "Accounts"
      }), "\n", _jsxs(_components.p, {
        children: ["The following fields can be used for ", _jsx(_components.code, {
          children: "Accounts"
        }), " for each respective action."]
      }), "\n", "\n", _jsxs("details", {
        children: [_jsxs("summary", {
          children: [_jsx(_components.code, {
            children: "Upsert"
          }), " ", _jsx(_components.em, {
            children: "(click to view table)"
          })]
        }), _jsxs(_components.table, {
          children: [_jsx(_components.thead, {
            children: _jsxs(_components.tr, {
              children: [_jsx(_components.th, {
                children: "Field Name"
              }), _jsx(_components.th, {
                children: "Notes"
              }), _jsx(_components.th, {
                children: "Required"
              })]
            })
          }), _jsxs(_components.tbody, {
            children: [_jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "accountNumber"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "applyCredit"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "applyCreditBalance"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "autoPay"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "batch"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "billCycleDay"
              }), _jsx(_components.td, {
                children: "integer"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "billToContact.firstName"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "billToContact.lastName"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "collect"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "communicationProfileId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "creditMemoReasonCode"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "creditMemoTemplateId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "crmId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "currency"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "debitMemoTemplateId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "documentDate"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoice"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceCollect"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceTargetDate"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceTemplateId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "name"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "notes"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "parentId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "paymentGateway"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "paymentTerm"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "runBilling"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "salesRep"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "sequenceSetId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "soldToSameAsBillTo"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "tagging"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "targetDate"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Class__NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "CustomerType__NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Department__NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "IntegrationId__NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "IntegrationStatus__NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Location__N"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Subsidiary__NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SyncDate__NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SynctoNetSuite__NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            })]
          })]
        })]
      }), "\n", _jsx("br", {}), "\n", _jsxs("details", {
        children: [_jsxs("summary", {
          children: [_jsx(_components.code, {
            children: "Update"
          }), " ", _jsx(_components.em, {
            children: "(click to view table)"
          })]
        }), _jsxs(_components.table, {
          children: [_jsx(_components.thead, {
            children: _jsxs(_components.tr, {
              children: [_jsx(_components.th, {
                children: "Field Name"
              }), _jsx(_components.th, {
                children: "Notes"
              })]
            })
          }), _jsxs(_components.tbody, {
            children: [_jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "autoPay"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "batch"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "billCycleDay"
              }), _jsx(_components.td, {
                children: "integer"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "billToContact"
              }), _jsx(_components.td, {
                children: "Contact"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "communicationProfileId"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "creditMemoTemplateId"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "crmId"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "debitMemoTemplateId"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "defaultPaymentMethodId"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceDeliveryPrefsEmail"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceDeliveryPrefsPrint"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceTemplateId"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "name"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "notes"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "parentId"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "paymentGateway"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "paymentTerm"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "salesRep"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "sequenceSetId"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "soldToContact"
              }), _jsx(_components.td, {
                children: "Contact"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "tagging"
              }), _jsx(_components.td, {
                children: "object"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Class__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "CustomerType__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Department__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "IntegrationId__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "IntegrationStatus__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Location__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Subsidiary__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SyncDate__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SynctoNetSuite__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Notes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["By default, ", _jsx(_components.code, {
            children: "autoPay"
          }), " is set to ", _jsx(_components.code, {
            children: "false"
          }), " for inserted ", _jsx(_components.code, {
            children: "Accounts"
          }), ", unless it's defined in the mappings for your sync.", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["If you would like to set ", _jsx(_components.code, {
                children: "autoPay"
              }), " to true, you must include either a ", _jsx(_components.code, {
                children: "paymentMethod"
              }), ", ", _jsx(_components.code, {
                children: "creditCard"
              }), ", or ", _jsx(_components.code, {
                children: "hmpCreditCardPaymentMethodId"
              }), " in your mappings."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["If either the ", _jsx(_components.code, {
            children: "workEmail"
          }), " or ", _jsx(_components.code, {
            children: "personalEmail"
          }), " fields are included in mappings, the account's email delivery preference is automatically set to ", _jsx(_components.code, {
            children: "true"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["To see more information on any respective field, view the Zuora API docs for ", _jsx(_components.a, {
            href: "https://www.zuora.com/developer/api-reference/#operation/POST_Account",
            children: "Creating an account"
          }), " or ", _jsx(_components.a, {
            href: "https://www.zuora.com/developer/api-reference/#operation/PUT_Account",
            children: "Updating an account"
          }), " via the API."]
        }), "\n"]
      }), "\n", _jsx("hr", {}), "\n", _jsx(_components.h4, {
        id: "subscriptions",
        children: "Subscriptions"
      }), "\n", _jsxs(_components.p, {
        children: ["The following fields can be used for ", _jsx(_components.code, {
          children: "Subscriptions"
        }), " for each respective action."]
      }), "\n", _jsxs("details", {
        children: [_jsxs("summary", {
          children: [_jsx(_components.code, {
            children: "Upsert"
          }), " ", _jsx(_components.em, {
            children: "(click to view table)"
          })]
        }), _jsxs(_components.table, {
          children: [_jsx(_components.thead, {
            children: _jsxs(_components.tr, {
              children: [_jsx(_components.th, {
                children: "Field Name"
              }), _jsx(_components.th, {
                children: "Notes"
              }), _jsx(_components.th, {
                children: "Required"
              })]
            })
          }), _jsxs(_components.tbody, {
            children: [_jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "accountKey"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "applyCredit"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "applyCreditBalance"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "autoRenew"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "collect"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "contractEffectiveDate"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "creditMemoReasonCode"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "customerAcceptanceDate"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "documentDate"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "externallyManagedBy"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "gatewayId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "initialTerm"
              }), _jsx(_components.td, {
                children: "integer"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "initialTermPeriodType"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoice"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceCollect"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceOwnerAccountKey"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceSeparately"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceTargetDate"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "notes"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "paymentMethodId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "renewalSetting"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "renewalTerm"
              }), _jsx(_components.td, {
                children: "integer"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "renewalTermPeriodType"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "runBilling"
              }), _jsx(_components.td, {
                children: "boolean"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "serviceActivationDate"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "subscriptionNumber"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "subscribetoRatePlans.productRatePlanId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "targetDate"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "termStartDate"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "termType"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "CpqBundleJsonId**QT"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "OpportunityCloseDate**QT"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "OpportunityName**QT"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "QuoteBusinessType**QT"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "QuoteNumber**QT"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "QuoteType**QT"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "IntegrationId**NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "IntegrationStatus**NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Project**NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SalesOrder**NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SyncDate__NS"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            })]
          })]
        })]
      }), "\n", _jsx("br", {}), "\n", _jsxs("details", {
        children: [_jsxs("summary", {
          children: [_jsx(_components.code, {
            children: "Update"
          }), " ", _jsx(_components.em, {
            children: "(click to view table)"
          })]
        }), _jsxs(_components.table, {
          children: [_jsx(_components.thead, {
            children: _jsxs(_components.tr, {
              children: [_jsx(_components.th, {
                children: "Field Name"
              }), _jsx(_components.th, {
                children: "Notes"
              })]
            })
          }), _jsxs(_components.tbody, {
            children: [_jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "add"
              }), _jsx(_components.td, {
                children: "Array of objects"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "applicationOrder"
              }), _jsx(_components.td, {
                children: "Array of strings"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "applyCredit"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "applyCreditBalance"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "autoRenew"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "clearingExistingBillToContactId"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "clearingExistingPaymentTerm"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "collect"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "creditMemoReasonCode"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "currentTerm"
              }), _jsx(_components.td, {
                children: "integer"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "currentTermPeriodType"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "documentDate"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "externallyManagedBy"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "includeExistingDraftDocItems"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "includeExistingDraftInvoiceItems"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoice"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceCollect"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceSeparately"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "invoiceTargetDate"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "notes"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "preview"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "previewType"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "renewalSetting"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "renewalTerm"
              }), _jsx(_components.td, {
                children: "integer"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "renewalTermPeriodType"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "runBilling"
              }), _jsx(_components.td, {
                children: "boolean"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "targetDate"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "termStartDate"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "termType"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "CpqBundleJsonId__QT"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "OpportunityCloseDate__QT"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "OpportunityName__QT"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "QuoteBusinessType__QT"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "QuoteNumber__QT"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "QuoteType__QT"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "IntegrationId__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "IntegrationStatus__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Project__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SalesOrder__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SyncDate__NS"
              }), _jsx(_components.td, {
                children: "string"
              })]
            })]
          })]
        })]
      }), "\n", "\n", _jsx(_components.p, {
        children: "Notes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Only the fields to be changed should be specified. If an empty field is submitted with this operation, the corresponding field in the account is emptied."
        }), "\n", _jsxs(_components.li, {
          children: ["To see more information on any respective field, view the Zuora API docs for ", _jsx(_components.a, {
            href: "https://www.zuora.com/developer/api-reference/#operation/POST_Subscription",
            children: "Creating a subscription"
          }), " or ", _jsx(_components.a, {
            href: "https://www.zuora.com/developer/api-reference/#operation/PUT_Subscription",
            children: "Updating a subscription"
          }), " via the API."]
        }), "\n"]
      }), "\n", _jsx("hr", {}), "\n", _jsx(_components.h4, {
        id: "usage",
        children: "Usage"
      }), "\n", _jsxs(_components.p, {
        children: ["The following fields can be used for ", _jsx(_components.code, {
          children: "Usage"
        }), " for each respective action."]
      }), "\n", _jsxs("details", {
        children: [_jsxs("summary", {
          children: [_jsx(_components.code, {
            children: "Insert"
          }), " ", _jsx(_components.em, {
            children: "(click to view table)"
          })]
        }), _jsxs(_components.table, {
          children: [_jsx(_components.thead, {
            children: _jsxs(_components.tr, {
              children: [_jsx(_components.th, {
                children: "Field Name"
              }), _jsx(_components.th, {
                children: "Type"
              }), _jsx(_components.th, {
                children: "Required"
              })]
            })
          }), _jsxs(_components.tbody, {
            children: [_jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "AccountId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "AccountNumber"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "ChargeId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "ChargeNumber"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Description"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "EndDateTime"
              }), _jsx(_components.td, {
                children: "string <date-time>"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Quantity"
              }), _jsx(_components.td, {
                children: "double"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "StartDateTime"
              }), _jsx(_components.td, {
                children: "string <date-time>"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SubmissionDateTime"
              }), _jsx(_components.td, {
                children: "string <date-time>"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SubscriptionId"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SubscriptionNumber"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {})]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "UOM"
              }), _jsx(_components.td, {
                children: "string"
              }), _jsx(_components.td, {
                children: "yes"
              })]
            })]
          })]
        })]
      }), "\n", _jsx("br", {}), "\n", _jsxs("details", {
        children: [_jsxs("summary", {
          children: [_jsx(_components.code, {
            children: "Update"
          }), " ", _jsx(_components.em, {
            children: "(click to view table)"
          })]
        }), _jsxs(_components.table, {
          children: [_jsx(_components.thead, {
            children: _jsxs(_components.tr, {
              children: [_jsx(_components.th, {
                children: "Field Name"
              }), _jsx(_components.th, {
                children: "Notes"
              })]
            })
          }), _jsxs(_components.tbody, {
            children: [_jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "EndDateTime"
              }), _jsx(_components.td, {
                children: "string <date-time>"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "Quantity"
              }), _jsx(_components.td, {
                children: "double"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "RbeStatus"
              }), _jsx(_components.td, {
                children: "string"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "StartDateTime"
              }), _jsx(_components.td, {
                children: "string <date-time>"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "SubmissionDateTime"
              }), _jsx(_components.td, {
                children: "string <date-time>"
              })]
            }), _jsxs(_components.tr, {
              children: [_jsx(_components.td, {
                children: "UOM"
              }), _jsx(_components.td, {
                children: "string"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Notes"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Any field with a type of ", _jsx(_components.code, {
            children: "string <date-time>"
          }), " must be in the format ", _jsx(_components.code, {
            children: "\"YYYY-MM-DDTHH:mm:ss\""
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["To see more information on any respective field, view the Zuora API docs for ", _jsx(_components.a, {
            href: "https://www.zuora.com/developer/api-reference/#operation/Object_POSTUsage",
            children: "Creating a usage record"
          }), " or ", _jsx(_components.a, {
            href: "https://www.zuora.com/developer/api-reference/#operation/Object_PUTUsage",
            children: "Updating a usage record"
          }), " via the API."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
